import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { ReservedSavedAddressType } from "@mapmycustomers/shared/enum/SavedAddressType";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { Modal, SearchField } from "@mapmycustomers/ui";

import {
  clickOnGlobalSearchItem,
  fetchUserRecents,
  hideGlobalSearch,
  performSearch,
  resetActiveQuery,
} from "@app/store/globalSearch/actions";
import {
  getGlobalSearchResults,
  getUserRecents,
  isUserRecentsLoading,
} from "@app/store/globalSearch/selectors";
import { getLeadGenPlan, isCurrentUserOwner, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import localSettings from "../../config/LocalSettings";
import GlobalSearchItemType from "../../enum/GlobalSearchItemType";
import Path from "../../enum/Path";
import GlobalSearchItem from "../../types/globalSearch/GlobalSearchItem";

import BottomInfo from "./components/BottomInfo";
import NoResults from "./components/NoResults";
import ResultList from "./components/ResultList";
import TypeSelector from "./components/TypeSelector";
import GlobalSearchFilter, { getGlobalSearchFilterValue } from "./enum/GlobalSearchFilter";
import styles from "./GlobalSearch.module.scss";
import buildGlobalSearchSections from "./util/buildGlobalSearchSections";
import useGlobalSearchKeyboard from "./util/useGlobalSearchKeyboard";
import useSavedLocationsSearch from "./util/useSavedLocationsSearch";

const GlobalSearchModal = ({
  isOwner,
  isReadOnlyMember,
  leadGenPlan,
  loading,
  onClickOnGlobalSearchItem,
  onFetchUserRecents,
  onHideGlobalSearch,
  onPerformSearch,
  onResetActiveQuery,
  searchResults,
  userRecents,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();
  const analytics = useExtendedAnalytics("Universal Search", useAnalytics());

  useEffect(() => {
    analytics.clicked([]);
  }, [analytics]);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<GlobalSearchFilter>(GlobalSearchFilter.ALL);
  const [setModeType, setSetModeType] = useState<ReservedSavedAddressType>();
  const handlePerformSearch = useDynamicCallback(
    (query: string, overrideFilter?: GlobalSearchFilter) => {
      const searchFilter = overrideFilter ?? filter;
      if (
        query.trim().length >= 3 &&
        searchFilter !== GlobalSearchFilter.SAVED_LOCATIONS &&
        !setModeType
      ) {
        onPerformSearch({
          entityTypes: getGlobalSearchFilterValue(searchFilter),
          query,
        });
      } else {
        onResetActiveQuery();
      }
    }
  );

  useChangeTracking(() => {
    analytics.clicked(["Filter"], { filter });
  }, [analytics, filter]);

  const [query, setQuery, resetQuery] = useStateWithDebouncedListener(
    [handlePerformSearch, 500],
    ""
  );

  const searchActivated = query.trim().length >= 3;
  const {
    addressesAnswered,
    addressItems,
    enabled: savedLocationsEnabled,
    handleSelect: handleSavedLocationSelect,
    leaveSetMode,
    savedLocationItems,
  } = useSavedLocationsSearch({
    filter,
    onHide: onHideGlobalSearch,
    onSetModeTypeChange: setSetModeType,
    query,
    setModeType,
    setQuery,
  });

  useEffect(() => {
    if (query.length && !setModeType) {
      localSettings.setGlobalSearchQuery(query);
    }
  }, [query, setModeType]);

  const handleSetFilter = useCallback(
    (filter: GlobalSearchFilter) => {
      setFilter(filter);
      searchRef.current?.focus();
      if (searchActivated) {
        handlePerformSearch(query, filter);
      }
    },
    [handlePerformSearch, query, setFilter, searchActivated]
  );

  useEffect(() => {
    onFetchUserRecents();
  }, [onFetchUserRecents]);

  const handleSelect = useCallback(
    (item: GlobalSearchItem) => {
      if (handleSavedLocationSelect(item)) {
        return;
      }
      if (GlobalSearchItemType.LEAD_FINDER === item.type) {
        analytics.clicked(["Find new leads based on search"]);
        if (leadGenPlan === LeadFinderSubscriptionPlan.NONE) {
          showLeadFinderUpsell(leadGenPlan, intl, isOwner);
        } else {
          history.push(`${Path.MAP}?findLeads&findLeadsQuery=${query}`);
          onHideGlobalSearch();
        }
      } else if (
        [GlobalSearchItemType.LOCAL_QUERY, GlobalSearchItemType.QUERY].includes(item.type)
      ) {
        analytics.clicked([" Clicked most recent search"]);
        handlePerformSearch(item.title);
        resetQuery(item.title);
      } else if (item.entity && item.entityType) {
        analytics.clicked(["Clicked row"]);
        onClickOnGlobalSearchItem({
          entity: item.entity,
          entityId: item.entity.id,
          entityType: item.entityType,
        });
        onHideGlobalSearch();
      }
    },
    [
      analytics,
      leadGenPlan,
      intl,
      isOwner,
      history,
      query,
      onHideGlobalSearch,
      handlePerformSearch,
      resetQuery,
      onClickOnGlobalSearchItem,
      handleSavedLocationSelect,
    ]
  );

  const sections = useMemo(
    () =>
      buildGlobalSearchSections({
        addressItems,
        filter,
        isReadOnlyMember,
        loading,
        savedLocationItems,
        searchActivated,
        searchResults,
        setModeType,
        userRecents,
      }),
    [
      addressItems,
      filter,
      isReadOnlyMember,
      loading,
      savedLocationItems,
      searchActivated,
      searchResults,
      setModeType,
      userRecents,
    ]
  );

  const items = useMemo(() => {
    const result = sections.flatMap(({ items }) => items);
    const newIndex = result.findIndex(
      ({ type }) =>
        type !== GlobalSearchItemType.LEAD_FINDER &&
        type !== GlobalSearchItemType.SET_SAVED_LOCATION
    );
    setSelectedIndex(newIndex === -1 ? 0 : newIndex);
    return result;
  }, [sections]);

  const noResults = setModeType
    ? addressesAnswered && !addressItems.length
    : !items.some(({ type }) => type !== GlobalSearchItemType.LEAD_FINDER);

  const handleEscape = useCallback(() => {
    if (query.length) {
      setQuery("");
    } else if (setModeType) {
      setSetModeType(undefined);
    } else {
      onHideGlobalSearch();
    }
  }, [onHideGlobalSearch, query, setModeType, setQuery]);

  useGlobalSearchKeyboard({
    containerClassName: styles.container,
    items,
    onEscape: handleEscape,
    onSelect: handleSelect,
    selectedIndex,
    setSelectedIndex,
  });

  const handleCancel = useCallback(() => {
    onHideGlobalSearch();
  }, [onHideGlobalSearch]);

  useEffect(() => setSelectedIndex(-1), [query, setSelectedIndex]);
  useEffect(() => searchRef.current?.focus(), [searchRef, setModeType]);

  return (
    <Modal
      centered
      className={styles.container}
      closeIcon={null}
      footer={null}
      keyboard={false}
      onCancel={handleCancel}
      open
      width="clamp(300px, 60vw, 750px)"
    >
      <SearchField
        allowClear
        className={styles.search}
        onChange={setQuery}
        placeholder={intl.formatMessage({
          id: "component.globalSearch.search.placeholder",
          defaultMessage: "Type at least 3 characters to begin search",
          description: "Search text on global search modal",
        })}
        ref={searchRef}
        size="large"
        value={query}
      />
      <TypeSelector
        onSelect={handleSetFilter}
        savedLocationsEnabled={savedLocationsEnabled}
        value={filter}
      />
      <div className={styles.contentWrapper}>
        <div className={cn({ [styles.listContainer]: loading || !noResults })}>
          <ResultList
            loading={loading}
            onClick={handleSelect}
            onSetModeCancel={leaveSetMode}
            onSetSelected={setSelectedIndex}
            sections={sections}
            selectedIndex={selectedIndex}
            setModeType={setModeType}
          />
        </div>
        {!loading && (noResults ? <NoResults /> : <BottomInfo />)}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  leadGenPlan: getLeadGenPlan(state),
  loading: isUserRecentsLoading(state),
  searchResults: getGlobalSearchResults(state),
  userRecents: getUserRecents(state),
});

const mapDispatchToProps = {
  onClickOnGlobalSearchItem: clickOnGlobalSearchItem,
  onFetchUserRecents: fetchUserRecents.request,
  onHideGlobalSearch: hideGlobalSearch,
  onPerformSearch: performSearch.request,
  onResetActiveQuery: resetActiveQuery,
};

export default connect(mapStateToProps, mapDispatchToProps)(GlobalSearchModal);
